import {
  blocksForDate, createDayBlock, createDayEnd, dayEndForDate, eventsForDate, isDateKey, isDayBlock, isDayEnd,
  isProposedBlock, isTimeKey, proposedBlocksForDate, timeToMinutes,
} from "./plannerItems";
import { busyEventSlots, firstFreeStart, minutesToTime, nextDateKey, DAY_START } from "./dayPlan";
import { isGhostEvent } from "./lumenCalendar";

const MAX_PLAN_BLOCKS = 20;
const MIN_LENGTH = 5;
const MAX_LENGTH = 8 * 60;
const DEFAULT_LENGTH = 30;

const newPlanId = () => `plan-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const parseKey = (key) => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const weekday = (key) => parseKey(key).toLocaleDateString("en-US", { weekday: "long" });
const endOf = (block) => timeToMinutes(block.start_time) + block.duration_minutes;

const failed = (reason) => ({ ok: false, reason, receipt: { kind: "plan", action: "failed", reason } });

function plansFor(items, date) {
  const firstDay = new Map();
  for (const b of (items || []).filter(isProposedBlock)) {
    const day = b.plan_date || b.date;
    const seen = firstDay.get(b.proposal);
    if (!seen || day < seen) firstDay.set(b.proposal, day);
  }
  return new Set([...firstDay].filter(([, d]) => d === date).map(([id]) => id));
}

export function planStatus(items, planId) {
  if (!planId) return "dismissed";
  if ((items || []).some((i) => isProposedBlock(i) && i.proposal === planId)) return "pending";
  if ((items || []).some((i) => isDayBlock(i) && !i.proposal && i.planned_by === planId)) return "accepted";
  return "dismissed";
}

export function pendingPlanId(items, dateKey) {
  return [...plansFor(items, dateKey)][0] || null;
}

function describeDay(items, dateKey, label, nowMinutes, carry = []) {
  const lines = [];
  const end = dayEndForDate(items, dateKey);
  const nowPart = nowMinutes == null ? "" : ` Now ${minutesToTime(nowMinutes)}.`;
  lines.push(`${label}: ${weekday(dateKey)} ${dateKey}. Day ends ${end}.${nowPart}`);
  const rows = [
    ...blocksForDate(items, dateKey).map((b) => ({
      at: b.start_time,
      text: `[id: ${b.id}] ${b.start_time} ${b.duration_minutes} min ${b.title} (${b.type}${b.done ? ", done" : ""}${
        !b.done && nowMinutes != null && endOf(b) <= nowMinutes ? ", not done and its time has passed" : ""})`,
    })),
    ...eventsForDate(items, dateKey).map((e) => ({
      at: e.start_time,
      text: `${e.start_time}-${e.end_time} ${e.title} (calendar event${isGhostEvent(e) ? ", not confirmed" : ""})`,
    })),
  ].sort((a, b) => a.at.localeCompare(b.at));
  if (!rows.length) lines.push("- nothing planned yet");
  rows.forEach((r) => lines.push(`- ${r.text}`));
  if (carry.length) {
    lines.push("- To carry over from today (not done, time passed):");
    carry.forEach((b) => lines.push(`  - [id: ${b.id}] ${b.duration_minutes} min ${b.title} (${b.type})`));
  }
  const waiting = proposedBlocksForDate(items, dateKey);
  if (waiting.length) {
    lines.push("- Your plan waiting for the user to accept:");
    waiting.forEach((b) => lines.push(`  - ${b.start_time} ${b.duration_minutes} min ${b.title} (${b.type})${b.from_block ? ` [from: ${b.from_block}]` : ""}`));
  }
  return lines.join("\n");
}

export function describeMyDayForLumen(items, todayKey, nowMinutes) {
  const tomorrowKey = nextDateKey(todayKey);
  const carry = nowMinutes == null ? [] : blocksForDate(items, todayKey).filter((b) => !b.done && endOf(b) <= nowMinutes);
  return [
    describeDay(items, todayKey, "Today", nowMinutes),
    describeDay(items, tomorrowKey, "Tomorrow", null, carry),
  ].join("\n");
}

export function describeMyDayNow(items, now = new Date()) {
  const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  return describeMyDayForLumen(items || [], todayKey, now.getHours() * 60 + now.getMinutes());
}

function withDayEnd(items, dateKey, endTime) {
  const marker = createDayEnd(dateKey, endTime);
  if (!marker) return items;
  return [...items.filter((i) => !(isDayEnd(i) && i.date === dateKey)), marker];
}

function cleanBlocks(rawBlocks, items, date) {
  const usable = new Map(
    (items || [])
      .filter((i) => isDayBlock(i) && !i.proposal && !i.done && i.date <= date)
      .map((b) => [b.id, b])
  );
  const used = new Set();
  return rawBlocks.slice(0, MAX_PLAN_BLOCKS).map((raw, index) => {
    const from = raw?.from && usable.has(raw.from) && !used.has(raw.from) ? usable.get(raw.from) : null;
    if (from) used.add(from.id);
    const title = String(raw?.title || from?.title || "").trim();
    const type = raw?.type === "break" ? "break" : raw?.type === "task" ? "task" : from?.type || "task";
    const asked = Number(raw?.duration_minutes);
    const length = Number.isFinite(asked) && asked > 0
      ? Math.min(MAX_LENGTH, Math.max(MIN_LENGTH, Math.round(asked)))
      : from?.duration_minutes || DEFAULT_LENGTH;
    const start = isTimeKey(raw?.start_time) ? timeToMinutes(raw.start_time) : null;
    return { title, type, length, start, from: from?.id || null, index };
  }).filter((b) => b.title);
}

export function buildPlan(update, items, { todayKey, nowMinutes }) {
  const date = update?.date;
  const tomorrowKey = nextDateKey(todayKey);
  if (!isDateKey(date)) return failed("missing_date");
  if (date !== todayKey && date !== tomorrowKey) return failed("only_today_or_tomorrow");
  const raw = Array.isArray(update?.blocks) ? update.blocks : [];
  let next = [...(items || [])];
  let dayEnd = dayEndForDate(next, date);
  if (isTimeKey(update?.day_end)) {
    dayEnd = update.day_end;
    next = withDayEnd(next, date, dayEnd);
  }
  const blocks = cleanBlocks(raw, next, date);
  if (!blocks.length) return failed("no_blocks");

  const replaced = plansFor(next, date);
  next = next.filter((i) => !(isProposedBlock(i) && replaced.has(i.proposal)));
  const planId = newPlanId();
  const busy = [
    ...busyEventSlots(next, date),
    ...blocksForDate(next, date).filter((b) => b.done).map((b) => ({ from: timeToMinutes(b.start_time), to: endOf(b) })),
  ];
  const earliest = date === todayKey && nowMinutes != null ? Math.ceil(nowMinutes / 15) * 15 : 0;
  const dayEndMinutes = timeToMinutes(dayEnd);
  const ordered = [...blocks].sort((a, b) => (a.start ?? Infinity) - (b.start ?? Infinity) || a.index - b.index);

  const placed = [];
  const overflow = [];
  let cursor = earliest;
  for (const block of ordered) {
    const want = Math.max(block.start ?? cursor, earliest);
    const start = firstFreeStart(want, block.length, [...busy, ...placed.map((p) => ({ from: p.start, to: p.start + p.length }))]);
    if (start + block.length > dayEndMinutes) { overflow.push(block); continue; }
    placed.push({ ...block, start });
    cursor = start + block.length;
  }
  if (!placed.length && !overflow.length) return failed("no_blocks");

  const proposed = [];
  for (const block of placed) {
    const made = createDayBlock({
      title: block.title, type: block.type, date, start_time: minutesToTime(block.start),
      duration_minutes: block.length, proposal: planId, from_block: block.from, plan_date: date,
    });
    if (made) proposed.push(made);
  }
  const spill = nextDateKey(date);
  const spillBusy = [
    ...busyEventSlots(next, spill),
    ...blocksForDate(next, spill).map((b) => ({ from: timeToMinutes(b.start_time), to: endOf(b) })),
  ];
  let spillCursor = Math.max(timeToMinutes(DAY_START), ...blocksForDate(next, spill).map(endOf));
  const spilled = [];
  for (const block of overflow) {
    const start = firstFreeStart(spillCursor, block.length, spillBusy);
    if (start + block.length > 24 * 60) continue;
    const made = createDayBlock({
      title: block.title, type: block.type, date: spill, start_time: minutesToTime(start),
      duration_minutes: block.length, proposal: planId, from_block: block.from, plan_date: date,
    });
    if (made) { spilled.push(made); spillBusy.push({ from: start, to: start + block.length }); spillCursor = start + block.length; }
  }
  if (!proposed.length && !spilled.length) return failed("no_blocks");

  const kept = new Set(placed.concat(overflow).map((b) => b.from).filter(Boolean));
  const replaces = blocksForDate(next, date).filter((b) => !b.done && !kept.has(b.id)).length;
  next = [...next, ...proposed, ...spilled];
  const first = proposed[0] || spilled[0];
  const last = proposed[proposed.length - 1] || spilled[spilled.length - 1];
  return {
    ok: true,
    planId,
    date,
    dayEnd,
    next,
    receipt: {
      kind: "plan", action: "plan_suggested", planId, date,
      count: proposed.length, start: first?.start_time || null,
      end: last ? minutesToTime(endOf(last)) : null,
      overflow: spilled.length, replaces,
      dayEndSet: isTimeKey(update?.day_end) ? update.day_end : null,
    },
  };
}

export function acceptPlan(items, planId) {
  const list = items || [];
  const plan = list.filter((i) => isProposedBlock(i) && i.proposal === planId);
  if (!plan.length) return failed("plan_gone");
  const mainDate = plan.map((b) => b.plan_date || b.date).sort()[0];
  const reused = new Map(
    plan.filter((b) => b.from_block).map((b) => [b.from_block, b])
  );
  const rivals = plansFor(list, mainDate);
  const next = [];
  for (const item of list) {
    if (isProposedBlock(item)) {
      if (item.proposal === planId) {
        const target = item.from_block && list.find((i) => i.id === item.from_block && isDayBlock(i) && !i.proposal && !i.done);
        if (target) continue;
        const { proposal, from_block, plan_date, ...rest } = item;
        next.push({ ...rest, planned_by: planId, timestamp: Date.now() });
      } else if (!rivals.has(item.proposal)) {
        next.push(item);
      }
      continue;
    }
    if (isDayBlock(item) && !item.done && reused.has(item.id)) {
      const p = reused.get(item.id);
      next.push({
        ...item, title: p.title, type: p.type, date: p.date, start_time: p.start_time,
        duration_minutes: p.duration_minutes, planned_by: planId, displaced_from: null, displaced_start: null,
        timestamp: Date.now(),
      });
      continue;
    }
    if (isDayBlock(item) && !item.done && item.date === mainDate) continue;
    next.push(item);
  }
  return { ok: true, next, receipt: { kind: "plan", action: "plan_accepted", planId, date: mainDate } };
}

export function dismissPlan(items, planId) {
  const list = items || [];
  const plan = list.filter((i) => isProposedBlock(i) && i.proposal === planId);
  if (!plan.length) return failed("plan_gone");
  const date = plan.map((b) => b.plan_date || b.date).sort()[0];
  return {
    ok: true,
    next: list.filter((i) => !(isProposedBlock(i) && i.proposal === planId)),
    receipt: { kind: "plan", action: "plan_dismissed", planId, date },
  };
}

export function setDayEndFromLumen(update, items, { todayKey }) {
  const date = update?.date || todayKey;
  const time = update?.time || update?.day_end;
  if (!isDateKey(date)) return failed("missing_date");
  if (date !== todayKey && date !== nextDateKey(todayKey)) return failed("only_today_or_tomorrow");
  if (!isTimeKey(time)) return failed("missing_time");
  return {
    ok: true,
    next: withDayEnd(items || [], date, time),
    receipt: { kind: "plan", action: "day_end_set", date, time },
  };
}

const dayLabel = (date, todayKey) =>
  date === todayKey ? "today" : date === nextDateKey(todayKey) ? "tomorrow" : weekday(date);

const PLAN_REASONS = {
  missing_date: "it needs a day",
  only_today_or_tomorrow: "I can only plan today or tomorrow",
  missing_time: "it needs a time",
  no_blocks: "there was nothing to plan",
  plan_gone: "that plan isn't waiting any more",
  not_sent: "the change didn't come through, so ask again",
};

export function describePlanReceipt(receipt, todayKey) {
  if (!receipt) return "";
  const when = receipt.date ? dayLabel(receipt.date, todayKey) : "";
  switch (receipt.action) {
    case "plan_suggested": {
      if (!receipt.count) {
        const n = receipt.overflow || 0;
        return `Plan for ${when}: nothing fits before your day ends, so ${n === 1 ? "the block moves" : `all ${n} blocks move`} to the next day. It stays grey in My Day until you accept it.`;
      }
      const parts = [`Plan for ${when}: ${receipt.count} block${receipt.count === 1 ? "" : "s"}`];
      if (receipt.start && receipt.end && receipt.count) parts[0] += `, ${receipt.start}–${receipt.end}`;
      if (receipt.dayEndSet) parts.push(`day ends ${receipt.dayEndSet}`);
      if (receipt.replaces) parts.push(`replaces ${receipt.replaces} unfinished block${receipt.replaces === 1 ? "" : "s"}`);
      if (receipt.overflow) parts.push(`${receipt.overflow} didn't fit and move${receipt.overflow === 1 ? "s" : ""} to the next day`);
      return `${parts.join(" · ")}. It stays grey in My Day until you accept it.`;
    }
    case "plan_accepted": return `Plan accepted for ${when}.`;
    case "plan_dismissed": return `Plan dismissed for ${when}.`;
    case "day_end_set": return `Your day ${when === "today" ? "today" : when} now ends at ${receipt.time}.`;
    case "failed": return `Couldn't update My Day: ${PLAN_REASONS[receipt.reason] || "the details didn't add up"}.`;
    default: return "";
  }
}
